import {
  getCurrentInstance,
  h,
  onBeforeUnmount, render,
} from 'vue'
import GlobalDialogLayout from '~/components/layout/GlobalDialogLayout.vue'

import type { IGlobalDialog, IGlobalDialogObject } from '~/types/global-dialog.type'

declare global {
  interface Window {
    globalDialogMap: Map<string, IGlobalDialogObject>
  }
}

export const useGlobalDialog = () => { 
  const isClient = () => typeof window !== 'undefined'
  if (isClient()) {
    // window 사용이유 > store 의존성 해소
    if (!window.globalDialogMap) window.globalDialogMap = new Map<string, IGlobalDialogObject>()
  }
  const dialogKeyList:string[] = []
  const instance = getCurrentInstance()

  const getGlobalObject = () => {
    if (isClient() && instance?.parent) {
      const parentType = instance.parent.type.__name
      // 명시적으로 허용된 레이아웃 컴포넌트들만 체크
      const allowedLayouts = [
        'GlobalDialogLayout',
        'MaintenanceModalLayout'
      ]
      
      if (parentType && allowedLayouts.includes(parentType)) {
        const key = String(instance.parent.props.randomKey)
        const globalObj = window.globalDialogMap.get(key)
        if (globalObj) {
          return globalObj
        }
      }
    }
  }

  const removeComponent = (globalObj: IGlobalDialogObject) => {
    globalObj.dialog = null
    render(null, globalObj.targetDiv)
    globalObj.targetDiv.remove()
  }

  const onDialogConfirm = (param?: unknown) => {
    const globalObj = getGlobalObject()
    if (globalObj) {
      globalObj.confirmCallback(param)
      removeComponent(globalObj)
    }
  }

  const onDialogCancel = (param?: unknown) => {
    const globalObj = getGlobalObject()
    if (globalObj) {
      globalObj.cancelCallback(param)
      removeComponent(globalObj)
    }
  }

  const onDialogClose = (param?: unknown) => {
    const globalObj = getGlobalObject()
    if (globalObj) {
      globalObj.closeCallback(param)
      removeComponent(globalObj)
    }
  }

  const getRandomKey = (componentName: string): string => {
    if (isClient() && window.globalDialogMap.get(componentName)) {
      return getRandomKey(`${componentName}_${Math.floor(Math.random() * 10)}`)
    }
    return componentName
  }

  const getResultObj = (key: string) => {
    return {
      onDialogConfirm: (callback?: (value: unknown) => void) => {
        if (callback && isClient()) {
          const globalObj = window.globalDialogMap.get(key)
          if (globalObj) {
            globalObj.confirmCallback = callback
          }
        }
        return getResultObj(key)
      },
      onDialogCancel: (callback?: (value: unknown) => void) => {
        if (callback && isClient()) {
          const globalObj = window.globalDialogMap.get(key)
          if (globalObj) {
            globalObj.cancelCallback = callback
          }
        }
        return getResultObj(key)
      },
      onDialogClose: (callback?: (value: unknown) => void) => {
        if (callback && isClient()) {
          const globalObj = window.globalDialogMap.get(key)
          if (globalObj) {
            globalObj.closeCallback = callback
          }
        }
        return
      }
    }
  }

  const dialog = (param: IGlobalDialog<unknown>) => {
    // 컴포넌트 명이 타입추론이 안되서 ignore 처리
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const key = getRandomKey(param.component['__name'] || 'random')
    if (isClient()) {
      const targetDiv = document.createElement('div')
      dialogKeyList.push(key)

      // 레이아웃 선택 (기본값: GlobalDialogLayout)
      const LayoutComponent = param.layout || GlobalDialogLayout

      // vue 버전문제인듯함
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const vNode = h(LayoutComponent, { randomKey: key }, () => h(param.component, param.componentProps))
      if (instance && instance.appContext) {
        vNode.appContext = instance.appContext
      }
      render(vNode, targetDiv)
      document.body.appendChild(targetDiv)

      window.globalDialogMap.set(key, {
        targetDiv,
        dialog: vNode,
        confirmCallback: () => { return },
        cancelCallback: () => { return },
        closeCallback: () => { return },
      })
    }

    return getResultObj(key)
  }

  onBeforeUnmount(()=> {
    if (isClient()) {
      dialogKeyList.forEach((key) => {
        window.globalDialogMap.delete(key)
      })
    }
  })

  return {
    dialog,
    onDialogConfirm,
    onDialogCancel,
    onDialogClose,
  }
}